import { Link } from 'react-router-dom'
import { Skeleton } from '@/components/ui/skeleton'
import { useSession } from '@/client/lib/auth'
import { useDashboardStats } from '@/client/modules/dashboard/hooks/useDashboardStats'
import { formatDistanceToNow } from 'date-fns'
import { Package, Bike, Search, Clock, AlertCircle } from 'lucide-react'

/**
 * Dashboard home — minimal overview.
 *
 * Philosophy: stop duplicating what lives on other pages. Just show the
 * four numbers that matter, the last few searches, and text links to
 * the detail pages. No big marketing cards, no nested chrome.
 */
export function DashboardPage() {
  const { data: session } = useSession()
  const { data: stats, isLoading, error } = useDashboardStats()

  const lastSyncDisplay = stats?.store.lastSyncAt
    ? formatDistanceToNow(stats.store.lastSyncAt * 1000, { addSuffix: true })
    : 'Never'

  const firstName = session?.user?.name?.split(' ')[0]

  return (
    <div className="space-y-10 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">
          {firstName ? `Welcome back, ${firstName}` : 'Welcome'}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          A quick snapshot of your search instance.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>Failed to load store status. Please refresh.</span>
        </div>
      )}

      {/* Flat stat strip — no card chrome */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 border-y border-border py-6">
        <Stat
          icon={<Package className="h-4 w-4" />}
          label="Products"
          value={isLoading ? null : (stats?.store.productCount ?? 0).toLocaleString()}
        />
        <Stat
          icon={<Bike className="h-4 w-4" />}
          label="Fitments"
          value={isLoading ? null : (stats?.store.fitmentCount ?? 0).toLocaleString()}
        />
        <Stat
          icon={<Search className="h-4 w-4" />}
          label="Searches (7d)"
          value={isLoading ? null : (stats?.overview?.searchesWeek ?? 0).toLocaleString()}
        />
        <Stat
          icon={<Clock className="h-4 w-4" />}
          label="Last sync"
          value={isLoading ? null : lastSyncDisplay}
          small
        />
      </div>

      {/* Recent activity — small, low-emphasis list */}
      {stats?.recentSearches && stats.recentSearches.length > 0 && (
        <section>
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
            Recent searches
          </h2>
          <ul className="space-y-1.5">
            {stats.recentSearches.map((r, i) => (
              <li
                key={`${r.query}-${i}`}
                className="flex items-baseline justify-between text-sm gap-4"
              >
                <Link
                  to={`/dashboard/search-preview?q=${encodeURIComponent(r.query)}`}
                  className="truncate hover:text-primary transition-colors"
                >
                  {r.query}
                </Link>
                <span className="text-xs text-muted-foreground shrink-0">
                  {formatDistanceToNow(r.timestamp * 1000, { addSuffix: true })}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Popular queries — lightweight inline list */}
      {stats?.popularQueries && stats.popularQueries.length > 0 && (
        <section>
          <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
            Popular this week
          </h2>
          <div className="flex flex-wrap gap-2">
            {stats.popularQueries.slice(0, 8).map((q) => (
              <Link
                key={q.query}
                to={`/dashboard/search-preview?q=${encodeURIComponent(q.query)}`}
                className="text-xs px-2.5 py-1 rounded-md bg-muted hover:bg-muted/70 transition-colors"
              >
                {q.query}
                <span className="text-muted-foreground ml-1.5">· {q.count}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Text-link row — minimal navigation */}
      <section>
        <h2 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">
          Jump to
        </h2>
        <nav className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
          <QuickLink to="/dashboard/search-preview">Try search</QuickLink>
          <QuickLink to="/dashboard/insights">Insights</QuickLink>
          <QuickLink to="/dashboard/store">Store &amp; sync</QuickLink>
          <QuickLink to="/dashboard/redirects">Redirects</QuickLink>
          <QuickLink to="/dashboard/settings">Settings</QuickLink>
        </nav>
      </section>
    </div>
  )
}

function Stat({
  icon,
  label,
  value,
  small,
}: {
  icon: React.ReactNode
  label: string
  value: string | null
  small?: boolean
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      {value === null ? (
        <Skeleton className="h-7 w-20 mt-1.5" />
      ) : (
        <div className={small ? 'text-sm font-medium mt-2' : 'text-2xl font-semibold mt-1'}>
          {value}
        </div>
      )}
    </div>
  )
}

function QuickLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link to={to} className="text-foreground/80 hover:text-primary transition-colors">
      {children}
    </Link>
  )
}
